<template>
  <div class="bracket-preview" v-if="brackets.length > 0">
    <div v-for="bracket in brackets" :key="bracket.branch_id" class="bracket-branch">
      <div v-if="!readonly" class="bracket-branch__title" :style="{ borderColor: branchColor(bracket.branch_id) }">
        {{ bracket.branch_name }}
      </div>
      <div class="bracket-scroll">
        <div class="bracket-wrapper">
          <!-- Groups column (only for advancement rule branches with groups) -->
          <div v-if="showGroupsColumn(bracket)" class="bracket-col bracket-groups-col">
            <div class="bracket-col__header">
              <span class="bracket-col__title">{{ t('bracket.groups') }}</span>
            </div>
            <div class="bracket-col__body">
              <div v-for="entry in groupEntries(bracket)" :key="entry.label"
                   class="bracket-group-entry">
                <span class="bracket-group-entry__letter">{{ entry.group }}</span><span class="bracket-group-entry__pos">{{ entry.position }}</span>
              </div>
            </div>
          </div>
          <!-- Connector: groups → first round -->
          <div v-if="showGroupsColumn(bracket) && groupedRounds(bracket).length > 0" class="bracket-col bracket-connector-col">
            <div class="bracket-col__header"></div>
            <div class="bracket-col__body">
              <svg class="bracket-connector__svg" viewBox="0 0 28 100" preserveAspectRatio="none">
                <line v-for="(line, li) in groupToRoundLines(bracket)" :key="li"
                  :x1="0" :y1="line.y1" :x2="28" :y2="line.y2"
                  vector-effect="non-scaling-stroke"/>
              </svg>
            </div>
          </div>
          <!-- Rounds with inter-round connectors -->
          <template v-for="(roundGroup, ri) in groupedRounds(bracket)" :key="ri">
            <!-- Connector: prev round → this round -->
            <div v-if="ri > 0" class="bracket-col bracket-round-connector-col">
              <div class="bracket-col__header"></div>
              <div class="bracket-col__body">
                <svg class="bracket-connector__svg" viewBox="0 0 24 100" preserveAspectRatio="none">
                  <line v-for="(line, li) in roundConnectorLines(groupedRounds(bracket)[ri - 1], roundGroup, Math.max(...groupedRounds(bracket).map(r => r.matches.length)))" :key="li"
                    :x1="0" :y1="line.y1" :x2="24" :y2="line.y2"
                    vector-effect="non-scaling-stroke"/>
                </svg>
              </div>
            </div>
            <!-- Round column -->
            <div class="bracket-col bracket-round-col">
              <div class="bracket-col__header">
                <span class="bracket-col__title">{{ roundGroup.name }}</span>
                <span v-if="readonly" class="bracket-series-chip">{{ roundGroup.series === 1 ? t('bracket.singleGame') : t('bracket.seriesWins', { n: roundGroup.series }) }}</span>
                <div v-else class="bracket-series-picker">
                  <button v-for="opt in [1, 2, 3, 4]" :key="opt"
                    class="series-btn" :class="{ 'series-btn--active': roundGroup.series === opt }"
                    @click="onSeriesChange(roundGroup.roundKey, opt)"
                  >{{ opt === 1 ? t('bracket.singleGame') : t('bracket.seriesWins', { n: opt }) }}</button>
                </div>
              </div>
              <div class="bracket-col__body">
                <div
                  v-for="(match, matchIdx) in roundGroup.matches"
                  :key="match.id"
                  class="bracket-match"
                  :class="{ 'bracket-match--placeholder': match.is_placeholder }"
                >
                  <div class="bracket-slot" :class="{ 'bracket-slot--bye': match.team_a?.role === 'bye' && branchHasPrelims(bracket), 'bracket-slot--editable': isAdmin }" :style="match.team_a?.role === 'bye' && branchHasPrelims(bracket) ? { color: byeColor } : {}" @click="onSlotClick(bracket.branch_id, roundGroup.resolvedRoundType, matchIdx, 'home', match.team_a)">
                    <span v-if="match.team_a?.group || (match.team_b?.group && match.team_a?.logo && !match.team_a?.is_placeholder)" class="bracket-seed" :style="!match.team_a?.group ? { visibility: 'hidden' } : {}">{{ match.team_a?.group ? `${match.team_a.group}-${match.team_a.group_position}` : '·' }}</span>
                    <img v-if="match.team_a?.logo && !match.team_a?.is_placeholder" :src="getTeamImage(match.team_a.logo)" alt="" class="bracket-team-logo" />
                    <span class="bracket-team-name">{{ match.team_a?.name ? (match.team_a.is_placeholder ? match.team_a.name : removeTrailingNumbers(match.team_a.name)) : 'TBD' }}</span>
                    <span v-if="match.team_a?.role === 'bye' && branchHasPrelims(bracket)" class="bracket-bye-badge" :style="{ background: byeBg, color: byeColor }">BYE</span>
                    <span v-if="getMatchWins(match)" class="bracket-wins" :class="{ 'bracket-wins--winner': getMatchWins(match)!.teamA > getMatchWins(match)!.teamB }">{{ getMatchWins(match)!.teamA }}</span>
                    <v-icon v-if="isAdmin" size="12" class="bracket-assign-icon">mdi-pencil</v-icon>
                  </div>
                  <div class="bracket-vs">vs</div>
                  <div class="bracket-slot" :class="{ 'bracket-slot--editable': isAdmin }" @click="onSlotClick(bracket.branch_id, roundGroup.resolvedRoundType, matchIdx, 'away', match.team_b)">
                    <span v-if="match.team_b?.group || (match.team_a?.group && match.team_b?.logo && !match.team_b?.is_placeholder)" class="bracket-seed" :style="!match.team_b?.group ? { visibility: 'hidden' } : {}">{{ match.team_b?.group ? `${match.team_b.group}-${match.team_b.group_position}` : '·' }}</span>
                    <img v-if="match.team_b?.logo && !match.team_b?.is_placeholder" :src="getTeamImage(match.team_b.logo)" alt="" class="bracket-team-logo" />
                    <span class="bracket-team-name">{{ match.team_b?.name ? (match.team_b.is_placeholder ? match.team_b.name : removeTrailingNumbers(match.team_b.name)) : 'TBD' }}</span>
                    <span v-if="getMatchWins(match)" class="bracket-wins" :class="{ 'bracket-wins--winner': getMatchWins(match)!.teamB > getMatchWins(match)!.teamA }">{{ getMatchWins(match)!.teamB }}</span>
                    <v-icon v-if="isAdmin" size="12" class="bracket-assign-icon">mdi-pencil</v-icon>
                  </div>
                  <div class="bracket-series-info" v-if="match.series > 1">
                    {{ t('bracket.bestOf', { n: match.series }) }}
                  </div>
                </div>
              </div>
            </div>
          </template>
        </div>
        <!-- Placement matches below bracket, right-aligned under final column -->
        <div v-if="branchPlacementMatches(bracket).length > 0" class="bracket-placement-row">
          <div
            v-for="pm in branchPlacementMatches(bracket)"
            :key="pm.place"
            class="bracket-placement"
          >
            <div class="bracket-placement__title">{{ pm.name }}</div>
            <div class="bracket-match" :class="{ 'bracket-match--placeholder': !resolvePlacementTeams(pm).home.id && !resolvePlacementTeams(pm).away.id }">
              <div class="bracket-slot">
                <img v-if="resolvePlacementTeams(pm).home.logo" :src="getTeamImage(resolvePlacementTeams(pm).home.logo)" alt="" class="bracket-team-logo" />
                <span class="bracket-team-name">{{ resolvePlacementTeams(pm).home.name }}</span>
                <span v-if="resolvePlacementTeams(pm).home.id && resolvePlacementTeams(pm).away.id" class="bracket-wins" :class="{ 'bracket-wins--winner': getPlacementWins(pm).home > getPlacementWins(pm).away }">{{ getPlacementWins(pm).home }}</span>
              </div>
              <div class="bracket-vs">vs</div>
              <div class="bracket-slot">
                <img v-if="resolvePlacementTeams(pm).away.logo" :src="getTeamImage(resolvePlacementTeams(pm).away.logo)" alt="" class="bracket-team-logo" />
                <span class="bracket-team-name">{{ resolvePlacementTeams(pm).away.name }}</span>
                <span v-if="resolvePlacementTeams(pm).home.id && resolvePlacementTeams(pm).away.id" class="bracket-wins" :class="{ 'bracket-wins--winner': getPlacementWins(pm).away > getPlacementWins(pm).home }">{{ getPlacementWins(pm).away }}</span>
              </div>
              <div class="bracket-series-info" v-if="pm.series > 1">
                {{ t('bracket.bestOf', { n: pm.series }) }}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
  <div v-else class="bracket-empty">
    {{ t('advRules.bracketEmpty') }}
  </div>
</template>

<script setup lang="ts">
import { computed, inject, type Ref } from 'vue';
import { useI18n } from 'vue-i18n';
import type { AdvancementRule, PlayoffBranch, PlayoffBracketResult, PlayoffMatch, GroupResult, PlacementMatch } from '@/types/playoff';
import { generateBracket } from '@/composables/usePlayoffBracket';
import { getTeamImage } from '@/utils/imageUtils';
import { removeTrailingNumbers } from '@/utils/teamUtils';

const props = withDefaults(defineProps<{
  rules: AdvancementRule[];
  branches: PlayoffBranch[];
  groupCount: number;
  readonly?: boolean;
  isAdmin?: boolean;
  groupResults?: GroupResult[] | null;
  seriesWins?: Record<string, Record<string, number>> | null;
  placementMatches?: PlacementMatch[];
  playoffEvents?: any[];
}>(), { readonly: false, isAdmin: false, groupResults: null, seriesWins: null, placementMatches: () => [], playoffEvents: () => [] });

const emit = defineEmits<{
  (e: 'update:series', payload: { roundKey: string; series: number }): void;
  (e: 'slotClick', payload: { branchId: number | null; roundType: string; seriesIndex: number; side: 'home' | 'away'; label: string; hasTeam: boolean }): void;
}>();

const { t } = useI18n({ useScope: 'global' });

const leagueHex = inject<Ref<string>>('leagueHex');

function hexToRgb(hex: string): string {
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);
  return `${r}, ${g}, ${b}`;
}

const byeColor = computed(() => leagueHex?.value || '#4caf50');
const byeBg = computed(() => {
  const hex = leagueHex?.value;
  if (!hex) return 'rgba(46, 125, 50, 0.12)';
  return `rgba(${hexToRgb(hex)}, 0.12)`;
});

interface RoundGroup {
  name: string;
  roundKey: string;
  resolvedRoundType: string;
  series: number;
  matches: PlayoffMatch[];
}

interface ConnectorLine {
  y1: number;
  y2: number;
}

const onSeriesChange = (rk: string, series: number) => {
  emit('update:series', { roundKey: rk, series });
};

/**
 * Resolve bracket round_key → event-compatible roundType (same logic as mergeEventTeams).
 */
function resolveEventRoundType(roundKey: string, matches: PlayoffMatch[], branch: PlayoffBranch): string {
  if (roundKey === 'preliminary') return 'prelim';

  const total = branch.rounds.length;
  let ri = -1;
  if (roundKey === 'final') ri = total - 1;
  else if (roundKey === 'semifinal') ri = total - 2;
  else if (roundKey === 'quarterfinal') ri = total - 3;
  else if (roundKey.startsWith('round_')) ri = parseInt(roundKey.replace('round_', ''), 10) - 1;

  if (ri >= 0 && ri < total) return branch.rounds[ri].name;

  // Fallback: strip trailing number from match round_name
  const sampleName = matches[0]?.round_name || '';
  return sampleName.replace(/\s+\d+$/, '');
}

function onSlotClick(branchId: number, roundType: string, seriesIndex: number, side: 'home' | 'away', team: any) {
  if (!props.isAdmin) return;
  const label = team?.name || 'TBD';
  const hasTeam = !!(team && !team.is_placeholder && team.id);
  emit('slotClick', { branchId, roundType, seriesIndex, side, label, hasTeam });
}

function getMatchWins(match: PlayoffMatch) {
  if (!match.team_a?.id || !match.team_b?.id) return null;
  if (!props.seriesWins) return { teamA: 0, teamB: 0 };
  const key = [match.team_a.id, match.team_b.id].sort().join('_');
  const wins = props.seriesWins[key];
  return {
    teamA: wins?.[match.team_a.id] || 0,
    teamB: wins?.[match.team_b.id] || 0,
  };
}

const brackets = computed<PlayoffBracketResult[]>(() => {
  if (props.branches.length === 0) return [];
  return props.branches.map(branch => {
    const bracket = generateBracket(branch, props.rules, props.groupResults ?? null, props.groupCount);
    if (props.playoffEvents?.length) {
      mergeEventTeams(bracket, props.playoffEvents, branch);
    }
    return bracket;
  });
});

/**
 * After bracket generation, overlay actual team data from playoff events
 * onto placeholder slots (e.g. "Vítěz Předkolo 2" → actual team name).
 */
function mergeEventTeams(bracket: PlayoffBracketResult, events: any[], branch: PlayoffBranch) {
  // Build event lookup: "roundType_seriesIndex" → first event with teams
  const eventMap = new Map<string, any>();
  for (const ev of events) {
    const pm = ev.playoffMeta;
    if (!pm || pm.branchId !== bracket.branch_id) continue;
    const key = `${pm.roundType}_${pm.seriesIndex}`;
    if (!eventMap.has(key)) eventMap.set(key, ev);
  }

  // Group bracket matches by round_key to determine per-round seriesIndex
  const roundGroups = new Map<string, PlayoffMatch[]>();
  for (const m of bracket.matches) {
    if (!roundGroups.has(m.round_key)) roundGroups.set(m.round_key, []);
    roundGroups.get(m.round_key)!.push(m);
  }

  for (const [roundKey, matches] of roundGroups) {
    // Map bracket round_key → event playoffMeta.roundType (must use config names, not i18n)
    const roundType = resolveEventRoundType(roundKey, matches, branch);

    matches.forEach((match, idx) => {
      const ev = eventMap.get(`${roundType}_${idx}`);
      if (!ev) return;

      // Replace placeholder home team with actual event data
      if (match.team_a?.is_placeholder) {
        const team = ev.meta?.homeTeam;
        if (team && typeof team === 'object' && team._id) {
          match.team_a = {
            ...match.team_a,
            id: String(team._id),
            name: team.shortName || team.name || match.team_a.name,
            logo: team.imageUrl || '',
            is_placeholder: false,
          };
        }
      }

      // Replace placeholder away team with actual event data
      if (match.team_b?.is_placeholder) {
        const team = ev.meta?.awayTeam;
        if (team && typeof team === 'object' && team._id) {
          match.team_b = {
            ...match.team_b,
            id: String(team._id),
            name: team.shortName || team.name || match.team_b.name,
            logo: team.imageUrl || '',
            is_placeholder: false,
          };
        }
      }
    });
  }
}

const branchColorMap: Record<string, string> = {
  bg: '#6a9f3a',
  ba: '#e8a735',
  bt: '#2a9d8f',
  bpk: '#d63384',
  bgr: '#757575',
  red: '#e53935',
  blue: '#1e88e5',
  green: '#43a047',
  orange: '#fb8c00',
  purple: '#8e24aa',
  teal: '#00897b',
  grey: '#757575',
};

const branchColor = (branchId: number): string => {
  const branch = props.branches.find(b => b.id === branchId);
  return branchColorMap[branch?.color || 'grey'] || '#757575';
};

function branchPlacementMatches(bracket: PlayoffBracketResult): PlacementMatch[] {
  if (!props.placementMatches?.length) return [];
  const branchId = bracket.branch_id;
  return props.placementMatches.filter(pm => {
    const src = pm.teams_from || (pm as any).teamsFrom || '';
    return src.startsWith(`branch_${branchId}_`);
  });
}

function placementSlotLabel(pm: PlacementMatch, slot: 'a' | 'b'): string {
  const src = pm.teams_from || (pm as any).teamsFrom || '';
  if (src.endsWith('_sf_losers')) {
    return slot === 'a' ? t('bracket.loserSemifinalN', { n: 1 }) : t('bracket.loserSemifinalN', { n: 2 });
  }
  if (src.endsWith('_final_loser')) {
    return slot === 'a' ? t('bracket.loserFinalLabel') : 'TBD';
  }
  return 'TBD';
}

function resolvePlacementTeams(pm: PlacementMatch): { home: { id: string; name: string; logo: string }; away: { id: string; name: string; logo: string } } {
  const placementRound = `placement_${pm.place}`;
  const ev = props.playoffEvents?.find(e =>
    e.playoffMeta?.roundType === 'placement' &&
    e.playoffMeta?.round === placementRound &&
    e.playoffMeta?.gameNumber === 1
  );
  if (!ev) {
    return {
      home: { id: '', name: placementSlotLabel(pm, 'a'), logo: '' },
      away: { id: '', name: placementSlotLabel(pm, 'b'), logo: '' },
    };
  }
  const ht = ev.meta?.homeTeam;
  const at = ev.meta?.awayTeam;
  return {
    home: ht && typeof ht === 'object' && ht._id
      ? { id: String(ht._id), name: ht.shortName || ht.name || placementSlotLabel(pm, 'a'), logo: ht.imageUrl || '' }
      : { id: '', name: placementSlotLabel(pm, 'a'), logo: '' },
    away: at && typeof at === 'object' && at._id
      ? { id: String(at._id), name: at.shortName || at.name || placementSlotLabel(pm, 'b'), logo: at.imageUrl || '' }
      : { id: '', name: placementSlotLabel(pm, 'b'), logo: '' },
  };
}

function getPlacementWins(pm: PlacementMatch): { home: number; away: number } {
  if (!props.seriesWins) return { home: 0, away: 0 };
  const teams = resolvePlacementTeams(pm);
  if (!teams.home.id || !teams.away.id) return { home: 0, away: 0 };
  const key = [teams.home.id, teams.away.id].sort().join('_');
  const wins = props.seriesWins[key];
  return {
    home: wins?.[teams.home.id] || 0,
    away: wins?.[teams.away.id] || 0,
  };
}

const groupedRounds = (bracket: PlayoffBracketResult): RoundGroup[] => {
  const roundOrder = ['preliminary', 'quarterfinal', 'semifinal', 'final'];
  const groups = new Map<string, PlayoffMatch[]>();

  for (const match of bracket.matches) {
    const key = match.round_key;
    if (!groups.has(key)) groups.set(key, []);
    groups.get(key)!.push(match);
  }

  const branch = props.branches.find(b => b.id === bracket.branch_id);

  const result: RoundGroup[] = [];
  const customRounds = [...groups.keys()].filter(k => k.startsWith('round_')).sort();

  // Custom rounds first (Osmifinále etc.)
  for (const ck of customRounds) {
    const matches = groups.get(ck);
    if (matches?.length) {
      const rrt = branch ? resolveEventRoundType(ck, matches, branch) : matches[0].round_name.replace(/ \d+$/, '');
      result.push({ name: matches[0].round_name.replace(/ \d+$/, ''), roundKey: ck, resolvedRoundType: rrt, series: matches[0].series, matches });
    }
  }

  for (const key of roundOrder) {
    const matches = groups.get(key);
    if (matches?.length) {
      const name = key === 'preliminary' ? t('bracket.preliminaryHeader')
        : key === 'quarterfinal' ? t('bracket.quarterfinal')
        : key === 'semifinal' ? t('bracket.semifinal')
        : t('bracket.final');
      const rrt = branch ? resolveEventRoundType(key, matches, branch) : name;
      result.push({ name, roundKey: key, resolvedRoundType: rrt, series: matches[0].series, matches });
    }
  }

  return result;
};

function isLosersBranch(bracket: PlayoffBracketResult): boolean {
  const branch = props.branches.find(b => b.id === bracket.branch_id);
  const tf = branch?.teams_from ?? '';
  return tf.startsWith('prelim_losers') || tf.startsWith('qf_losers') || tf.startsWith('sf_losers');
}

function branchHasPrelims(bracket: PlayoffBracketResult): boolean {
  const branch = props.branches.find(b => b.id === bracket.branch_id);
  return (branch?.preliminary_rounds ?? 0) > 0 && !isLosersBranch(bracket);
}

function isAdvancementBranch(bracket: PlayoffBracketResult): boolean {
  const branch = props.branches.find(b => b.id === bracket.branch_id);
  const tf = branch?.teams_from ?? '';
  return tf.startsWith('adv') || tf === 'advancement_rules';
}

function showGroupsColumn(bracket: PlayoffBracketResult): boolean {
  return isAdvancementBranch(bracket) && props.groupCount > 0;
}

interface GroupEntry {
  group: string;
  position: number;
  label: string;
}

function groupEntries(bracket: PlayoffBracketResult): GroupEntry[] {
  const branch = props.branches.find(b => b.id === bracket.branch_id);
  if (!branch || props.groupCount <= 0) return [];
  const tpg = Math.floor(branch.team_count / props.groupCount);
  const hasPrelims = (branch.preliminary_rounds ?? 0) > 0;
  const startPos = hasPrelims ? 2 : 1;
  const entries: GroupEntry[] = [];
  for (let g = 0; g < props.groupCount; g++) {
    const letter = String.fromCharCode(65 + g);
    for (let p = startPos; p <= tpg; p++) {
      entries.push({ group: letter, position: p, label: `${letter}${p}` });
    }
  }
  return entries;
}

function groupToRoundLines(bracket: PlayoffBracketResult): ConnectorLine[] {
  const rounds = groupedRounds(bracket);
  if (rounds.length === 0) return [];
  const firstRound = rounds[0];
  const entries = groupEntries(bracket);
  const total = entries.length;
  const nMatches = firstRound.matches.length;
  const lines: ConnectorLine[] = [];
  for (let i = 0; i < entries.length; i++) {
    const entry = entries[i];
    const y1 = (i + 0.5) / total * 100;
    const matchIdx = firstRound.matches.findIndex(m =>
      (m.team_a?.group === entry.group && m.team_a?.group_position === entry.position) ||
      (m.team_b?.group === entry.group && m.team_b?.group_position === entry.position)
    );
    if (matchIdx >= 0) {
      const match = firstRound.matches[matchIdx];
      const isTeamA = match.team_a?.group === entry.group && match.team_a?.group_position === entry.position;
      const slotOffset = isTeamA ? 0.3 : 0.7;
      const y2 = (matchIdx + slotOffset) / nMatches * 100;
      lines.push({ y1, y2 });
    }
  }
  return lines;
}

function roundConnectorLines(prev: RoundGroup, curr: RoundGroup, maxN: number): ConnectorLine[] {
  const n1 = prev.matches.length;
  const n2 = curr.matches.length;
  const lines: ConnectorLine[] = [];
  // Fixed offset in viewBox units — same body height for all columns
  const teamOffset = 20 / maxN;

  // Build a map: destMatchId → ordered list of source indices (to know team_a vs team_b)
  const feeders: Record<string, number[]> = {};
  for (let i = 0; i < n1; i++) {
    const destId = prev.matches[i].winner_to;
    if (!destId) continue;
    if (!feeders[destId]) feeders[destId] = [];
    feeders[destId].push(i);
  }

  for (let i = 0; i < n1; i++) {
    const y1 = (i + 0.5) / n1 * 100;
    const match = prev.matches[i];
    if (match.winner_to) {
      const destIdx = curr.matches.findIndex(m => m.id === match.winner_to);
      if (destIdx >= 0) {
        const siblings = feeders[match.winner_to!] || [i];
        let isTeamA: boolean;
        if (siblings.length >= 2) {
          isTeamA = siblings.indexOf(i) === 0;
        } else {
          // Single feeder: determine target slot from destination match
          const dest = curr.matches[destIdx];
          if (!dest.team_b) {
            isTeamA = true;
          } else if (dest.team_b.is_placeholder && !dest.team_a?.is_placeholder) {
            isTeamA = false;
          } else if (dest.team_a?.is_placeholder && !dest.team_b.is_placeholder) {
            isTeamA = true;
          } else if (dest.team_a?.role === 'bye') {
            isTeamA = false;
          } else {
            isTeamA = true;
          }
        }
        const matchCenter = (destIdx + 0.5) / n2 * 100;
        const y2 = matchCenter + (isTeamA ? -teamOffset : teamOffset);
        lines.push({ y1, y2 });
      }
    } else if (n2 > 0) {
      const destIdx = Math.min(Math.floor(i / 2), n2 - 1);
      const isFirst = i % 2 === 0;
      const matchCenter = (destIdx + 0.5) / n2 * 100;
      const y2 = matchCenter + (isFirst ? -teamOffset : teamOffset);
      lines.push({ y1, y2 });
    }
  }
  return lines;
}
</script>

<style lang="scss" scoped>
.bracket-preview {
  padding: 0 16px 8px;
}

.bracket-branch {
  margin-bottom: 16px;
  overflow-x: auto;
}

.bracket-branch__title {
  font-size: 0.82rem;
  font-weight: 700;
  color: rgba(var(--v-theme-sheetText), 0.8);
  border-left: 3px solid;
  padding-left: 8px;
  margin-bottom: 10px;
}

.bracket-scroll {
  display: inline-flex;
  flex-direction: column;
}

.bracket-wrapper {
  display: flex;
  align-items: stretch;
  padding-bottom: 4px;
}

.bracket-placement-row {
  display: flex;
  justify-content: flex-end;
  margin-top: 16px;
  gap: 12px;
}

.bracket-placement {
  min-width: 140px;
}

.bracket-placement__title {
  font-size: 0.68rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.4px;
  color: rgba(var(--v-theme-sheetText), 0.4);
  text-align: center;
  margin-bottom: 6px;
}

.bracket-col {
  display: flex;
  flex-direction: column;
  flex-shrink: 0;
}

.bracket-col__header {
  min-height: 44px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: flex-end;
  gap: 2px;
  padding-bottom: 6px;
}

.bracket-col__title {
  font-size: 0.68rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.4px;
  color: rgba(var(--v-theme-sheetText), 0.4);
  text-align: center;
}

.bracket-col__body {
  flex: 1;
  display: flex;
  flex-direction: column;
  justify-content: space-around;
  gap: 10px;
}

.bracket-connector-col {
  width: 28px;
  margin-left: -10px;
}

.bracket-round-connector-col {
  width: 28px;
}

.bracket-groups-col {
  min-width: 40px;
}

.bracket-group-entry {
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.7rem;
  color: rgba(var(--v-theme-sheetText), 0.5);
  padding: 2px 4px;
}

.bracket-group-entry__letter {
  font-weight: 700;
  color: rgba(var(--v-theme-sheetText), 0.6);
  margin-right: 1px;
}

.bracket-group-entry__pos {
  font-weight: 500;
}

.bracket-connector__svg {
  width: 100%;
  height: 100%;

  line {
    stroke: rgba(var(--v-theme-sheetText), 0.15);
    stroke-width: 1.5;
  }
}

.bracket-round-col {
  min-width: 140px;
}

.bracket-series-chip {
  font-size: 0.6rem;
  font-weight: 600;
  padding: 1px 7px;
  border-radius: 99px;
  background: rgba(var(--v-theme-sheetText), 0.08);
  color: rgba(var(--v-theme-sheetText), 0.5);
  letter-spacing: 0.3px;
}

.bracket-series-picker {
  display: flex;
  gap: 3px;
}

.series-btn {
  font-size: 0.6rem;
  padding: 1px 6px;
  border-radius: 99px;
  border: 1px solid rgba(var(--v-theme-sheetText), 0.12);
  background: transparent;
  color: rgba(var(--v-theme-sheetText), 0.4);
  cursor: pointer;
  transition: all 0.15s;

  &--active {
    background: rgba(var(--v-theme-sheetText), 0.08);
    color: rgba(var(--v-theme-sheetText), 0.8);
    border-color: rgba(var(--v-theme-sheetText), 0.25);
    font-weight: 600;
  }
}

.bracket-match {
  border: 1px solid rgba(var(--v-theme-sheetText), 0.1);
  border-radius: 8px;
  padding: 6px 8px;
  background: rgba(var(--v-theme-sheetText), 0.02);

  &--placeholder {
    border-style: dashed;
    border-color: rgba(var(--v-theme-sheetText), 0.08);
  }
}

.bracket-slot {
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: 0.75rem;
  color: rgba(var(--v-theme-sheetText), 0.7);
  min-height: 28px;

  &--editable {
    cursor: pointer;
    border-radius: 4px;
    padding: 0 4px;
    margin: 0 -4px;
    transition: background 0.15s;

    &:hover {
      background: rgba(var(--v-theme-sheetText), 0.05);
    }
  }
}

.bracket-assign-icon {
  opacity: 0.25;
  margin-left: 2px;
  flex-shrink: 0;
}

.bracket-seed {
  font-size: 0.65rem;
  font-weight: 700;
  color: rgba(var(--v-theme-sheetText), 0.35);
  min-width: 18px;
}

.bracket-team-name {
  flex: 1;
  font-family: 'Roboto Condensed', sans-serif;
  font-size: 0.7rem;
  font-weight: 600;
  color: rgba(var(--v-theme-sheetText), 0.6);
  line-height: 1.15;
  text-transform: uppercase;
  letter-spacing: 0.03em;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.bracket-bye-badge {
  font-size: 0.58rem;
  font-weight: 700;
  padding: 0 4px;
  border-radius: 3px;
  letter-spacing: 0.4px;
  // background + color set via inline :style
}

.bracket-team-logo {
  width: 28px;
  height: 28px;
  object-fit: contain;
  border-radius: 3px;
  flex-shrink: 0;
}

.bracket-wins {
  font-family: 'Roboto Condensed', sans-serif;
  font-size: 20px;
  font-weight: 700;
  color: rgba(var(--v-theme-sheetText), 0.35);
  min-width: 14px;
  text-align: center;
  flex-shrink: 0;

  &--winner {
    color: rgba(var(--v-theme-sheetText), 1);
  }
}

.bracket-vs {
  font-size: 0.6rem;
  color: rgba(var(--v-theme-sheetText), 0.25);
  text-align: center;
  padding: 1px 0;
}

.bracket-series-info {
  font-size: 0.6rem;
  color: rgba(var(--v-theme-sheetText), 0.3);
  text-align: right;
  margin-top: 2px;
}

.bracket-empty {
  padding: 16px;
  text-align: center;
  font-size: 0.82rem;
  color: rgba(var(--v-theme-sheetText), 0.35);
}
</style>
